import Cookies from "js-cookie";
import { React, useState, useEffect, useMemo, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";

import Navbar from "../../../Components/Navbar";
import { fetchProfile } from "../../../utils/fetchProfile";

import MapLayout from "../MapLayout";
import L from "leaflet";
import * as turf from "@turf/turf";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { Button, Box, Card, CircularProgress, Typography } from "@mui/material";
import { fetchProvincesFormatted } from "../../Dashboard/DataUtil";
import SearchBarDashboard from "../../../Components/SearchBarDashboard";

const provinceMap = {
  11: "ACEH",
  12: "SUMATERA_UTARA",
  13: "SUMATERA_BARAT",
  14: "RIAU",
  15: "JAMBI",
  16: "SUMATERA_SELATAN",
  17: "BENGKULU",
  18: "LAMPUNG",
  19: "KEPULAUAN_BANGKA_BELITUNG",
  21: "KEPULAUAN_RIAU",
  31: "DKI_JAKARTA",
  32: "JAWA_BARAT",
  33: "JAWA_TENGAH",
  34: "DAERAH_ISTIMEWA_YOGYAKARTA",
  35: "JAWA_TIMUR",
  36: "BANTEN",
  51: "BALI",
  52: "NUSA_TENGGARA_BARAT",
  53: "NUSA_TENGGARA_TIMUR",
  61: "KALIMANTAN_BARAT",
  62: "KALIMANTAN_TENGAH",
  63: "KALIMANTAN_SELATAN",
  64: "KALIMANTAN_TIMUR",
  65: "KALIMANTAN_UTARA",
  71: "SULAWESI_UTARA",
  72: "SULAWESI_TENGAH",
  73: "SULAWESI_SELATAN",
  74: "SULAWESI_TENGGARA",
  75: "GORONTALO",
  76: "SULAWESI_BARAT",
  81: "MALUKU",
  82: "MALUKU_UTARA",
  91: "PAPUA",
  92: "PAPUA_BARAT",
};

const RectangleIcon = ({ color }) => (
  <div
    style={{
      width: "20px",
      height: "20px",
      backgroundColor: color || "gray",
      marginRight: "3px",
    }}
  ></div>
);

const getFillColor = (count) => {
  return count > 1000
    ? "#073956ff"
    : count > 500
    ? "#094d75ff"
    : count > 250
    ? "#0b5b89ff"
    : count > 100
    ? "#237aacff"
    : count > 75
    ? "#449ed2ff"
    : count > 50
    ? "#62afdcff"
    : count > 10
    ? "#90ccefff"
    : "#f7fbff";
};

const styleFeatureWithTotal = (feature) => {
  const totalPlan = feature.properties.total_plan_sum || 0;

  return {
    color: "#4f4f4fff",
    weight: 1,
    fillColor: getFillColor(totalPlan),
    fillOpacity: 0.9,
  };
};

function MapSurveyorProvince() {
  const { provinceId } = useParams();
  const accessToken = Cookies.get("access_token") || "";
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notAllowAccessPage, setNotAllowAccessPage] = useState(false);
  const [dataProfile, setDataProfile] = useState([]);
  const [dataProvince, setDataProvince] = useState([]);

  const isDashboardFetchedRef = useRef(false);
  const isGeoJSONFetchedRef = useRef(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedSurveyId = queryParams.get("survey_id") || "";
  const selectedPeriodeId = queryParams.get("periode") || "";
  const selectedArea = queryParams.get("area") || "";

  const [dataDashboard, setDataDashboard] = useState([]);
  const [dataUserSurveyor, setDataUserSurveyor] = useState([]);
  const [dataUserSurveyorOptions, setDataUserSurveyorOptions] = useState([]);
  const [dataUserQcOptions, setDataUserQcOptions] = useState([]);

  const [surveyDataId, setSurveyDataId] = useState([]);

  const [filteredGeoJSONData, setFilteredGeoJSONData] = useState(null);
  const [totalPlanCount, setTotalPlanCount] = useState(0);

  const [mapPosition, setMapPosition] = useState([-2.548, 112.544]);
  const [mapZoom, setMapZoom] = useState(5);

  const fetchRegencyGeoJSONInBatch = async (provinceId, limit = 10) => {
    const baseUrl = process.env.REACT_APP_API_MAP_URL;
    const token = Cookies.get("access_token") || "";

    let allFeatures = [];
    let skip = 0;
    let totalItems = null;

    while (true) {
      const url = `${baseUrl}/api/v1/regencies?province_id=${provinceId}&skip=${skip}&limit=${limit}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error("Failed to fetch regency geojson:", response.statusText);
        break;
      }

      const { items, total_items } = await response.json();

      if (totalItems === null) {
        totalItems = total_items;
      }

      if (!items || items.length === 0) break;

      allFeatures.push(...items);
      skip += limit;

      if (allFeatures.length >= totalItems) break;
    }

    return allFeatures;
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchAllData = async () => {
      if (!selectedSurveyId || !selectedPeriodeId || !provinceId) return;

      setIsLoading(true);
      setFilteredGeoJSONData(null);
      isDashboardFetchedRef.current = false;
      isGeoJSONFetchedRef.current = false;

      try {
        const params = new URLSearchParams({
          is_area: false,
          survey_id: selectedSurveyId,
          periode: selectedPeriodeId,
          province_id: provinceId,
        });

        const dashboardRes = await fetch(
          `${
            process.env.REACT_APP_API_URL
          }/api/v1/dashboard/audit-summary?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            signal: controller.signal,
          }
        );

        const dashboardData = await dashboardRes.json();
        const dashboardItems = dashboardRes.ok ? dashboardData.data : [];

        setDataDashboard(dashboardItems);
        isDashboardFetchedRef.current = true;

        const regencyFeatures = await fetchRegencyGeoJSONInBatch(
          provinceId,
          10
        );

        const enrichedFeatures = regencyFeatures.map((feature) => {
          const regencyCode = `${feature.properties.province_id}${feature.properties.kabkot_id}`;
          const match = dashboardItems.find(
            (item) => item.code === regencyCode
          );
          const totalPlanSum = match ? match.total_plan : 0;

          return {
            ...feature,
            properties: {
              ...feature.properties,
              total_plan_sum: totalPlanSum,
              store_count: totalPlanSum,
            },
          };
        });

        setFilteredGeoJSONData({
          type: "FeatureCollection",
          features: enrichedFeatures,
        });

        isGeoJSONFetchedRef.current = true;
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Data fetching failed:", err);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();

    return () => {
      controller.abort();
    };
  }, [selectedSurveyId, selectedPeriodeId, provinceId]);

  useEffect(() => {
    if (!filteredGeoJSONData || !filteredGeoJSONData.features.length) return;

    const bbox = turf.bbox(filteredGeoJSONData);

    if (!bbox.every(isFinite)) {
      console.warn("Invalid bbox from geojson:", bbox);
      return;
    }

    const center = [(bbox[1] + bbox[3]) / 2, (bbox[0] + bbox[2]) / 2];
    const bboxWidth = bbox[2] - bbox[0];
    const bboxHeight = bbox[3] - bbox[1];
    const maxDim = Math.max(bboxWidth, bboxHeight);
    const zoom = Math.max(Math.floor(10 - Math.log2(maxDim)), 5);

    setMapPosition(center);
    setMapZoom(zoom);
  }, [filteredGeoJSONData]);

  useEffect(() => {
    if (dataDashboard && dataDashboard.length > 0) {
      const total = dataDashboard.reduce(
        (sum, item) => sum + item.total_plan,
        0
      );
      setTotalPlanCount(total);
    }
  }, [dataDashboard]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchProfile(accessToken, setDataProfile, setNotAllowAccessPage, []);
      fetchProvincesFormatted(accessToken, setDataProvince);
    }, 100);
    return () => {
      clearTimeout(handler);
    };
  }, [accessToken]);

  const handleProvinceIdChange = (event, newValue) => {
    let id = "";
    let valueId = "";
    if (newValue) {
      id = newValue.id;
      valueId = newValue.province_code;
    }
    window.location.href = `/map-surveyor/${id}?survey_id=${selectedSurveyId}&periode=${selectedPeriodeId}&area=${selectedArea}`;
  };

  const onEachProvinceFeature = (feature, layer) => {
    const provinceName = feature.properties.name;
    const totalPlanSum = feature.properties.total_plan_sum || 0;

    const tooltip = layer
      .bindTooltip(`${totalPlanSum} Toko`, {
        permanent: true,
        direction: "center",
        className: "province-label",
      })
      .getTooltip();

    layer.on({
      mouseover: () => {
        tooltip.setContent(`${provinceName} (${totalPlanSum} Toko)`);
      },
      mouseout: () => {
        tooltip.setContent(`${totalPlanSum} Toko`);
      },
      click: (e) => {
        const mhid = feature.properties.mhid;
        const regencyId = feature.properties.kabkot_id;
        if (provinceId && mhid) {
          window.location.href = `/map-surveyor/${provinceId}/${regencyId}/${mhid}?survey_id=${selectedSurveyId}&periode=${selectedPeriodeId}&area=${selectedArea}`;
        } else {
          console.error("Missing province or regency ID");
        }
      },
    });
  };

  const exportToPDF = () => {
    setIsLoading(true);

    const inputElements = document.getElementsByClassName("printToPdfArea");

    if (inputElements.length === 0) {
      console.error('Element with class "printToPdfArea" not found');
      setIsLoading(false);
      return;
    }

    const input = inputElements[0];

    html2canvas(input, { scale: 1.5 })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/jpeg", 0.7);
        const imgWidth = canvas.width * 0.75;
        const imgHeight = canvas.height * 0.75;
        const pdf = new jsPDF("p", "mm", "a4");

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const title = `peta_sebaran_toko_${(
          provinceMap[provinceId] || "Unknown Province"
        )
          .toLowerCase()
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")}_0_toko`;

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);

        const titleLines = pdf.splitTextToSize(title, pageWidth - 20);

        let yPosition = 6;
        titleLines.forEach((line) => {
          const textWidth = pdf.getTextWidth(line);
          const xPosition = (pageWidth - textWidth) / 2;
          pdf.text(line, xPosition, yPosition);
          yPosition += 6;
        });

        const imageTopPosition = yPosition + 10;
        const imageHeight = (imgHeight * pageWidth) / imgWidth;

        if (imageHeight <= pageHeight - imageTopPosition) {
          pdf.addImage(
            imgData,
            "JPEG",
            0,
            imageTopPosition,
            pageWidth,
            imageHeight
          );
        } else {
          let heightLeft = imageHeight;
          let position = imageTopPosition;

          while (heightLeft > 0) {
            pdf.addImage(imgData, "JPEG", 0, position, pageWidth, imageHeight);
            heightLeft -= pageHeight;
            if (heightLeft > 0) {
              pdf.addPage();
              position = 0;
            }
          }
        }

        pdf.save(`${title}.pdf`);
      })
      .catch((error) => {
        console.error("Error exporting to PDF:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const MapIcon = (mapData) => {
    let iconUrl = "";

    switch (mapData.store_status) {
      case "0":
        iconUrl =
          "https://img.icons8.com/?size=100&id=7880&format=png&color=FA5252";
        break;
      case "1":
        iconUrl =
          "https://img.icons8.com/?size=100&id=7880&format=png&color=5D9C59";
        break;
      default:
        iconUrl = "default-icon-url.png";
        break;
    }

    const icon = L.icon({
      iconUrl: iconUrl,
      iconSize: [10, 10],
      className: "custom-icon",
    });

    return icon;
  };

  const detailMasterData = (mapData) => {
    return (
      <article>
        <h4>Kode Outlet : {mapData.id}</h4>
        <h4>Nama Outlet : {mapData.name}</h4>
        <h4>Tipe Outlet : {mapData.tipe_toko}</h4>
        <h4>Tipe Survey : {mapData.tipe_survey}</h4>
        <h4>Alamat : {mapData.address}</h4>
      </article>
    );
  };

  const formattedProvinceName = useMemo(() => {
    return (provinceMap[provinceId] || "Unknown Province")
      .toLowerCase()
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }, [provinceId]);

  if (notAllowAccessPage) {
    return (
      <div>
        <Navbar
          active="Dashboard"
          initial={dataProfile.initial}
          role_code={dataProfile.role_code}
        />
        <div>Not allow to access this page</div>
      </div>
    );
  }
  return (
    <div>
      <Navbar
        active="Dashboard"
        initial={dataProfile.initial}
        role_code={dataProfile.role_code}
      />
      <div className="content printToPdfArea" style={{ padding: "1rem" }}>
        <div className="legend">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: { xs: "flex-start", sm: "space-between" },
              gap: 2,
              alignItems: "center",
            }}
          >
            <Button
              className="button-pros"
              sx={{
                backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
                border: "1px solid hsl(186, 89.90%, 27.30%)",
                borderRadius: "4px",
                color: "#fff",
                fontSize: "14px",
                height: "fit-content",
                textTransform: "none",
                "&:hover": {
                  backgroundImage: "linear-gradient(#0991a2, #057d8c)",
                },
              }}
              disabled={isLoading}
              onClick={() => (window.location.href = "/dashboard-dev")}
            >
              Back to Previous
            </Button>
            <Typography
              variant="h4"
              sx={{
                fontSize: 20,
                fontFamily: "Montserrat",
                fontWeight: 700,
              }}
            >
              Peta sebaran toko di provinsi {formattedProvinceName}{" "}
              {totalPlanCount ? `(${totalPlanCount} Toko)` : ""}
            </Typography>
            <Button
              className="button-pros"
              sx={{
                backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
                border: "1px solid hsl(186, 89.90%, 27.30%)",
                borderRadius: "4px",
                color: "#fff",
                fontSize: "14px",
                height: "fit-content",
                textTransform: "none",
                "&:hover": {
                  backgroundImage: "linear-gradient(#0991a2, #057d8c)",
                },
              }}
              onClick={exportToPDF}
              disabled={isLoading}
            >
              Download as PDF
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              gap: 2,
              alignItems: "center",
              mt: 2,
            }}
          >
            <SearchBarDashboard
              id={"area-autocomplete"}
              data={dataProvince}
              value={
                dataProvince.find((option) => option.id === provinceId) ?? null
              }
              handleChange={handleProvinceIdChange}
              label={"Provinsi"}
              disabled={isLoading}
              disableClearable={true}
              width={200}
            />
          </Box>
          <Card sx={{ m: 0.5, mt: 3, backgroundColor: "#f0fbfcff" }}>
            <div>
              {isLoading || !filteredGeoJSONData ? (
                <Box
                  sx={{
                    height: "700px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress size={60} thickness={5} />
                </Box>
              ) : (
                <MapLayout
                  markersData={{
                    data: surveyDataId,
                    icon: MapIcon,
                  }}
                  position={mapPosition}
                  zoom={mapZoom}
                  pathOptions={{
                    color: "#4f4f4fff",
                    weight: 1,
                    // fillOpacity: 0.9,
                  }}
                  scrollWheelZoom={true}
                  styleContainer={{ width: "100%", height: "700px" }}
                  renderDataDetail={detailMasterData}
                  geoJSONData={filteredGeoJSONData}
                  onEachFeature={onEachProvinceFeature}
                  needLegend={false}
                  needChoropleth={true}
                  styleFeature={styleFeatureWithTotal}
                />
              )}
            </div>
          </Card>
        </div>
      </div>
      <style>{`
        .province-label {
          background-color: transparent;
          border: none;
          box-shadow: none;
          font-weight: 900;
          font-size: 12px;
          color: #333;
            text-shadow: 
                1px 1px 0 white, 
            -1px 1px 0 white, 
                1px -1px 0 white, 
            -1px -1px 0 white; 
            }
      `}</style>
    </div>
  );
}

export default MapSurveyorProvince;
